import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';
import { LuClock, LuArrowRight, LuAlertCircle } from 'react-icons/lu';

const categories = ['all', 'adventure', 'nature', 'food', 'travel', 'culture', 'tips'];

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const baseUrl = axios.defaults.baseURL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
  return `${baseUrl}${url}`;
};

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('all');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog', activeCategory],
    queryFn: () => axios.get(`/api/blog${activeCategory !== 'all' ? `?category=${activeCategory}` : ''}`).then(r => r.data)
  });

  const posts = data?.posts || [];
  const featured = posts.length > 0 ? posts[0] : null;
  const rest = posts.length > 1 ? posts.slice(1) : [];

  return (
    <PageWrapper>
      <Helmet>
        <title>Wayanad Travel Guide & Journal – Villa Tesori | Local Insights</title>
        <meta name="description" content="Read travel stories, trekking guides, and local tips for Wayanad. Discover hidden waterfalls, tea plantation culture, and Kerala's best experiences from Villa Tesori." />
        <meta name="keywords" content="Wayanad travel guide, Kerala trekking stories, hidden gems Wayanad, best time to visit Wayanad, Wayanad food guide, luxury travel Kerala blog" />
        <meta property="og:title" content="The Villa Tesori Journal – Wayanad Travel & Stories" />
        <meta property="og:description" content="Local guides, cultural insights, and nature stories from the peaks of Wayanad." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/blog" />
        <link rel="canonical" href="https://villatesori.com/blog" />
      </Helmet>

      <PageHero
        scriptText="Journal & Stories"
        title="The Villa Tesori Journal"
        subtitle="From our hilltop in Vellarimala — travel stories, local guides, and Wayanad wisdom."
        bgImage="https://images.unsplash.com/photo-1448375240586-882707db888b?q=80&w=1600"
      />

      <section className="section" style={{ background: 'var(--cream-light)' }}>
        <div className="container">
          {/* Category Filter */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 64, flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button 
                key={cat} 
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '10px 24px', 
                  border: '1px solid',
                  borderColor: activeCategory === cat ? 'var(--forest)' : 'var(--mist)',
                  background: activeCategory === cat ? 'var(--forest)' : 'transparent',
                  color: activeCategory === cat ? 'var(--gold-light)' : 'var(--text-muted)',
                  fontFamily: 'Jost, sans-serif', 
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em', 
                  textTransform: 'uppercase',
                  cursor: 'pointer', 
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                  borderRadius: 0,
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div style={{ padding: '100px 0', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 20px' }} />
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: 'var(--text-muted)' }}>Fetching stories...</p>
            </div>
          ) : posts.length === 0 ? (
            <div style={{ padding: '80px 40px', textAlign: 'center', background: 'var(--white)', border: '1px solid var(--mist)' }}>
              <LuAlertCircle size={40} style={{ color: 'var(--gold)', marginBottom: 16 }} />
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', color: 'var(--forest)', marginBottom: 12 }}>No stories found</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>We haven't shared anything in this category yet. Stay tuned!</p>
              <button 
                className="btn btn-outline" 
                onClick={() => setActiveCategory('all')}
                style={{ padding: '10px 24px' }}
              >
                View All Stories
              </button>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featured && activeCategory === 'all' && (
                <div style={{ marginBottom: 80 }}>
                  <Link to={`/blog/${featured.slug}`} className="featured-card">
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) 1fr', gap: 0, overflow: 'hidden', border: '1px solid var(--mist)', background: 'var(--white)' }}>
                      <div className="img-overlay" style={{ height: 480 }}>
                        <img 
                          src={getImageUrl(featured.coverImage?.url) || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200'} 
                          alt={featured.title} 
                          style={{ transition: 'transform 1.2s ease' }}
                        />
                        <div style={{ position: 'absolute', top: 32, left: 32 }}>
                          <span className="badge badge-gold" style={{ textTransform: 'capitalize', padding: '6px 16px' }}>{featured.category}</span>
                        </div>
                      </div>
                      <div style={{ padding: '64px 60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                          <span style={{ height: 1, width: 30, background: 'var(--gold)' }} />
                          <span style={{ fontSize: '0.72rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.2em', fontFamily: 'Jost' }}>Featured Article</span>
                        </div>
                        <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.4rem', color: 'var(--forest)', marginBottom: 20, lineHeight: 1.2 }}>{featured.title}</h2>
                        <p style={{ color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 32, fontSize: '1rem' }}>{featured.excerpt}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 'auto' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            <LuClock size={14} /> {featured.readTime} min read
                          </span>
                          <span className="read-more-link" style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--forest)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            Read Story <LuArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Grid of posts */}
              <div className="grid-3">
                {(activeCategory === 'all' ? rest : posts).map(post => (
                  <Link key={post._id} to={`/blog/${post.slug}`} className="blog-card" style={{ textDecoration: 'none' }}>
                    <div className="img-overlay" style={{ height: 260, marginBottom: 24 }}>
                      <img src={getImageUrl(post.coverImage?.url) || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600'} alt={post.title} />
                      <div style={{ position: 'absolute', top: 16, left: 16 }}>
                        <span className="badge badge-gold" style={{ textTransform: 'capitalize', fontSize: '0.68rem' }}>{post.category}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {post.readTime} min read <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--gold)' }} /> {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                      </div>
                      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', marginBottom: 14, lineHeight: 1.3, color: 'var(--forest)' }}>{post.title}</h3>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: 20 }}>{post.excerpt.slice(0, 110)}...</p>
                      <span style={{ fontSize: '0.75rem', color: 'var(--forest)', fontWeight: 500, borderBottom: '1px solid var(--forest)', paddingBottom: 2 }}>Read Full Article</span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <style>{`
        .featured-card:hover img { transform: scale(1.05); }
        .featured-card:hover .read-more-link { gap: 12px; }
        .blog-card { transition: transform 0.3s ease; }
        .blog-card:hover { transform: translateY(-8px); }
        .blog-card h3 { transition: color 0.3s ease; }
        .blog-card:hover h3 { color: var(--gold); }
        .grid-3 { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 40px; }
        @media (max-width: 900px) {
          .featured-card > div { grid-template-columns: 1fr !important; }
          .featured-card .img-overlay { height: 300px !important; }
          .featured-card div[style^="padding"] { padding: 40px 30px !important; }
        }
      `}</style>
    </PageWrapper>
  );
}

